<template>
  <div class="payment-success-container">
    <!-- Success Icon with Animation -->
    <div class="success-icon-container">
      <div class="success-icon">
        <svg class="checkmark" viewBox="0 0 52 52">
          <circle class="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
          <path class="checkmark-check" fill="none" d="m14.1 27.2l7.1 7.2 16.7-16.8"/>
        </svg>
      </div>
    </div>

    <!-- Success Message -->
    <div class="success-message">
      <h1 class="success-title">{{ titleText }}</h1>
      <p class="success-subtitle">{{ subtitleText }}</p>

      <ol class="status-steps" aria-label="Payment processing steps">
        <li :class="stepClass('received')">
          <span class="step-dot" />
          <span>Payment received</span>
        </li>
        <li :class="stepClass('verifying')">
          <span class="step-dot" />
          <span>Verifying payment with gateway</span>
        </li>
        <li :class="stepClass('activating')">
          <span class="step-dot" />
          <span>{{ activationStepText }}</span>
        </li>
      </ol>

      <div v-if="isProcessing" class="activation-loader" aria-live="polite">
        <div class="activation-spinner" aria-hidden="true" />
        <span>{{ loaderText }}</span>
      </div>
      <p v-if="isProcessing" class="activation-note">Please do not close this tab.</p>
      <button v-if="showDashboardButton" type="button" class="success-cta" @click="goToDashboard">
        Go to dashboard
      </button>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { readEsewaContext, clearEsewaContext } from '@/utilities/esewaPayment';

export default {
  name: 'PaymentSuccess',
  data() {
    return {
      titleText: 'Processing your payment',
      subtitleText: 'We are verifying your transaction and activating your subscription.',
      step: 'received', // received | verifying | activating | completed | pending
      loaderText: 'Verifying payment...',
      isProcessing: true,
      showDashboardButton: false,
    };
  },
  computed: {
    isAddSlotFlow() {
      const context = readEsewaContext();
      const purpose = String(
        context?.referencedata?.transactionpurpose
          || context?.transactionpurpose
          || context?.checkoutpurpose
          || ''
      ).toUpperCase();
      return purpose === 'ADDSLOT';
    },
    activationStepText() {
      return this.isAddSlotFlow ? 'Confirming purchased slot' : 'Activating subscription';
    },
  },
  async mounted() {
    await this.handleEsewaCallback();
  },
  methods: {
    async handleEsewaCallback() {
      const callbackToken = String(this.$route.query?.data || '').trim();
      const context = readEsewaContext();

      if (!callbackToken || !context?.referenceid || !context?.referencedata) {
        this.step = 'activating';
        this.loaderText = 'Activating subscription...';
        this.titleText = 'Finishing setup';
        this.subtitleText = 'Payment appears complete. We are finishing your subscription setup.';
        return;
      }

      try {
        this.step = 'verifying';
        this.loaderText = 'Verifying payment with gateway...';
        const referencedata = {
          ...context.referencedata,
          callbackToken,
          propertycount: String(
            context.referencedata?.propertycount
              || context.propertycount
              || ''
          ),
          slotcount: String(
            context.referencedata?.slotcount
              || context.slotcount
              || ''
          ),
        };
        const callbackResponse = await apiService.esewaCallback(context.referenceid, referencedata);
        const result = callbackResponse?.data || {};
        if (String(result.transactionstatus || '').toUpperCase() === 'SUCCESS') {
          this.step = 'activating';
          this.loaderText = this.isAddSlotFlow ? 'Confirming purchased slot...' : 'Activating subscription...';
          await apiService.refreshSession();
          const subscriptionResponse = await apiService.getSubscription();
          if (subscriptionResponse?.data && typeof subscriptionResponse.data === 'object') {
            localStorage.setItem('SUBSCRIPTION_DATA', JSON.stringify(subscriptionResponse.data));
          }
          this.step = 'completed';
          if (this.isAddSlotFlow) {
            this.titleText = 'PURCHASED SLOT CONFIRMED';
            this.subtitleText = 'Your additional slot purchase has been confirmed.';
          } else {
            this.titleText = 'SUBSCRIPTION ACTIVATED';
            this.subtitleText = 'Your subscription is active and ready to use.';
          }
          this.showDashboardButton = true;
        } else {
          this.$router.replace('/payment/failed');
          return;
        }
      } catch (error) {
        console.error('Failed to process eSewa success callback:', error);
        this.$router.replace('/payment/failed');
        return;
      } finally {
        this.isProcessing = !this.showDashboardButton;
        clearEsewaContext();
      }
    },
    stepClass(name) {
      const order = { received: 1, verifying: 2, activating: 3, completed: 4, pending: 3 };
      const current = order[this.step] || 1;
      const target = order[name] || 1;
      return {
        'step-item': true,
        done: current > target,
        active: current === target,
      };
    },
    goToDashboard() {
      this.$router.push('/dashboard');
    },
  },
}
</script>

<style scoped>
.payment-success-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Success Icon */
.success-icon-container {
  margin-bottom: 40px;
  position: relative;
}

.success-icon {
  width: 140px;
  height: 140px;
  margin: 0 auto;
  animation: bounceIn 0.8s ease-out;
  position: relative;
}

.success-icon::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  opacity: 0.1;
  animation: pulse 2s infinite;
}

.checkmark {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 12px rgba(16, 185, 129, 0.3));
}

.checkmark-circle {
  stroke: #10b981;
  stroke-width: 4;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  animation: stroke 0.8s cubic-bezier(0.65, 0, 0.45, 1) forwards;
  fill: rgba(16, 185, 129, 0.05);
}

.checkmark-check {
  stroke: #10b981;
  stroke-width: 4;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: stroke 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
  stroke-linecap: round;
  stroke-linejoin: round;
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.95);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
}

/* Success Message */
.success-message {
  text-align: center;
  max-width: 560px;
}

.success-title {
  font-size: 3.5rem;
  font-weight: 800;
  color: #065f46;
  margin: 0 0 16px 0;
  animation: fadeInUp 0.8s ease-out 0.5s both;
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.success-subtitle {
  font-size: 1.25rem;
  color: #6b7280;
  margin: 0;
  animation: fadeInUp 0.8s ease-out 0.7s both;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.success-cta {
  margin-top: 1.25rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.65rem 1.2rem;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.24);
}

.activation-loader {
  margin-top: 1.1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.75rem;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.12);
  color: #065f46;
  font-size: 0.86rem;
  font-weight: 600;
}

.activation-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(6, 95, 70, 0.22);
  border-top-color: #059669;
  animation: activationSpin 0.8s linear infinite;
}

.activation-note {
  margin: 0.6rem 0 0;
  font-size: 0.82rem;
  color: #065f46;
  font-weight: 500;
}

.status-steps {
  list-style: none;
  padding: 0;
  margin: 1rem auto 0;
  width: 100%;
  max-width: 440px;
  display: grid;
  gap: 0.55rem;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.86rem;
  font-weight: 600;
}

.step-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #cbd5e1;
}

.step-item.active {
  color: #0f766e;
}

.step-item.active .step-dot {
  background: #0d9488;
  box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.14);
}

.step-item.done {
  color: #065f46;
}

.step-item.done .step-dot {
  background: #059669;
}

@keyframes activationSpin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Professional Card Effect */
.payment-success-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 600px;
  height: 60%;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 24px;
  box-shadow: 
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: -1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .payment-success-container {
    padding: 30px 16px;
  }

  .success-title {
    font-size: 2.8rem;
  }

  .success-subtitle {
    font-size: 1.1rem;
  }

  .success-icon {
    width: 120px;
    height: 120px;
  }

  .success-icon-container {
    margin-bottom: 32px;
  }
}

@media (max-width: 480px) {
  .payment-success-container {
    padding: 24px 16px;
  }

  .success-title {
    font-size: 2.2rem;
  }

  .success-subtitle {
    font-size: 1rem;
  }

  .success-icon {
    width: 100px;
    height: 100px;
  }

  .success-icon-container {
    margin-bottom: 28px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .success-title {
    color: #000;
  }
  
  .success-subtitle {
    color: #333;
  }
  
  .checkmark-circle,
  .checkmark-check {
    stroke: #000;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .success-icon,
  .success-title,
  .success-subtitle {
    animation: none;
  }
  
  .success-icon::before {
    animation: none;
  }
}
</style>
